<script setup lang="ts">
import { computed } from "vue";
import { useI18n } from "vue-i18n";
import Dialog from "primevue/dialog";
import Button from "primevue/button";
import type { ImportResult } from "../../composables/useCSVOperations";
import type { MaintenanceLanguage } from "../../types/TextMaintenanceTypes";

const props = defineProps<{
    visible: boolean;
    summary: ImportResult | null;
    availableLanguages: MaintenanceLanguage[];
}>();

const emit = defineEmits<{
    "update:visible": [value: boolean];
}>();

const { t } = useI18n();

const getLanguageName = (code: string): string => {
    const key = `languageNames.${code.toLowerCase()}`;
    const translated = t(key);
    return key === translated ? code.toUpperCase() : translated;
};

interface SummaryRow {
    code: string;
    name: string;
    updated: number;
    added: number;
    unchanged: number;
}

const rows = computed<SummaryRow[]>(() => {
    if (!props.summary) return [];
    return Object.entries(props.summary.perLanguage)
        .map(([code, counts]) => ({ code, name: getLanguageName(code), ...counts }))
        .sort((a, b) => a.name.localeCompare(b.name));
});

const changedLanguageCount = computed(() => rows.value.filter((r) => r.updated > 0 || r.added > 0).length);

const displayedLanguageNames = computed(() =>
    (props.summary?.displayedLanguages ?? []).map(getLanguageName).join(", ")
);

const hasNewIdentifiers = computed(() => (props.summary?.newIdentifiers.length ?? 0) > 0);
const hasUnmatched = computed(() => (props.summary?.unmatchedLanguages.length ?? 0) > 0);
const hasEncodingWarning = computed(() => (props.summary?.encodingWarningLanguages.length ?? 0) > 0);

const closeDialog = () => emit("update:visible", false);
</script>

<template>
    <Dialog
        :visible="visible"
        @update:visible="emit('update:visible', $event)"
        modal
        :header="t('textMaintenance.importSummary.title')"
        :style="{ width: '600px' }"
        data-cy="text-maintenance_import-summary-dialog"
    >
        <div v-if="summary" class="flex flex-col gap-4">
            <!-- Headline -->
            <p class="text-content font-medium m-0">
                {{
                    t("textMaintenance.importSummary.headline", {
                        imported: summary.imported,
                        updated: summary.updated,
                        languages: changedLanguageCount,
                    })
                }}
            </p>

            <!-- Per-language table -->
            <div>
                <p class="text-sm font-medium text-content-muted mb-2">
                    {{ t("textMaintenance.importSummary.perLanguageTitle") }}
                </p>
                <table class="import-summary-table" data-cy="text-maintenance_import-summary-table">
                    <thead>
                        <tr>
                            <th>{{ t("textMaintenance.importSummary.colLanguage") }}</th>
                            <th class="num">{{ t("textMaintenance.importSummary.colUpdated") }}</th>
                            <th class="num">{{ t("textMaintenance.importSummary.colAdded") }}</th>
                            <th class="num">{{ t("textMaintenance.importSummary.colUnchanged") }}</th>
                        </tr>
                    </thead>
                    <tbody>
                        <tr
                            v-for="row in rows"
                            :key="row.code"
                            :class="{ 'has-changes': row.updated > 0 || row.added > 0 }"
                            :data-cy="`text-maintenance_import-summary-row-${row.code}`"
                        >
                            <td>
                                {{ row.name }}
                                <i
                                    v-if="summary.encodingWarningLanguages.includes(row.code)"
                                    class="pi pi-exclamation-triangle text-orange-500 ml-2"
                                    :title="t('textMaintenance.importSummary.encodingWarning')"
                                ></i>
                            </td>
                            <td class="num">{{ row.updated }}</td>
                            <td class="num">{{ row.added }}</td>
                            <td class="num text-content-muted">{{ row.unchanged }}</td>
                        </tr>
                        <tr v-if="rows.length === 0">
                            <td colspan="4" class="text-content-muted">
                                {{ t("textMaintenance.importSummary.noMatchedLanguages") }}
                            </td>
                        </tr>
                    </tbody>
                </table>
            </div>

            <!-- New identifiers -->
            <div v-if="hasNewIdentifiers" class="bg-green-50 border border-green-200 rounded p-4">
                <p class="text-sm font-medium text-green-800 mb-2">
                    {{ t("textMaintenance.importSummary.newIdentifiersTitle", { count: summary.newIdentifiers.length }) }}
                </p>
                <div class="flex flex-wrap gap-2">
                    <code v-for="id in summary.newIdentifiers" :key="id" class="text-green-800">{{ id }}</code>
                </div>
            </div>

            <!-- Unmatched columns -->
            <div v-if="hasUnmatched" class="bg-orange-50 border border-orange-200 rounded p-4">
                <p class="text-sm font-medium text-orange-800 mb-1">
                    {{ t("textMaintenance.importSummary.unmatchedColumnsTitle") }}
                </p>
                <p class="text-sm text-orange-800 mb-2">
                    {{ t("textMaintenance.importSummary.unmatchedColumnsNote") }}
                </p>
                <div class="flex flex-wrap gap-2">
                    <code v-for="lang in summary.unmatchedLanguages" :key="lang" class="text-orange-800">{{ lang }}</code>
                </div>
            </div>

            <!-- Encoding warning -->
            <div v-if="hasEncodingWarning" class="bg-orange-50 border border-orange-200 rounded p-4">
                <p class="text-sm text-orange-800 m-0">
                    <i class="pi pi-exclamation-triangle mr-2"></i>
                    {{ t("textMaintenance.importSummary.encodingWarning") }}
                </p>
            </div>

            <!-- Applied-beyond-view note + save reminder -->
            <div class="bg-blue-50 border border-blue-200 rounded p-4">
                <p class="text-sm text-blue-800 mb-1">
                    <i class="pi pi-info-circle mr-2"></i>
                    <span v-if="displayedLanguageNames">
                        {{ t("textMaintenance.importSummary.appliedBeyondViewNote", { languages: displayedLanguageNames }) }}
                    </span>
                    <span v-else>
                        {{ t("textMaintenance.importSummary.appliedNote") }}
                    </span>
                </p>
                <p class="text-sm font-medium text-blue-800 m-0">
                    {{ t("textMaintenance.importSummary.saveReminder") }}
                </p>
            </div>
        </div>

        <template #footer>
            <Button
                :label="t('buttons.ok')"
                @click="closeDialog"
                data-cy="text-maintenance_import-summary-ok"
            />
        </template>
    </Dialog>
</template>

<style scoped>
.import-summary-table {
    width: 100%;
    border-collapse: collapse;
    font-size: 0.9rem;
}

.import-summary-table th,
.import-summary-table td {
    padding: 0.4rem 0.6rem;
    border-bottom: 1px solid var(--p-surface-200);
    text-align: left;
}

.import-summary-table th {
    font-weight: 600;
    color: var(--p-text-color);
}

.import-summary-table .num {
    text-align: right;
    font-variant-numeric: tabular-nums;
}

.import-summary-table tr.has-changes td {
    font-weight: 600;
}

code {
    background-color: rgba(0, 0, 0, 0.08);
    padding: 2px 6px;
    border-radius: 3px;
    font-family: "Monaco", "Menlo", "Ubuntu Mono", "Consolas", "source-code-pro", monospace;
    font-size: 0.85rem;
}
</style>
